import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placePopover } from './popoverPosition';
import './Tooltip.css';

// ─── TooltipBox ─────────────────────────────────────────────────────────────
// The tooltip box itself, positioned against `anchor` (a DOM element) and rendered
// in a portal on <body> so it sits over everything and isn't clipped by scrolling
// or overflow containers. Used by <Tooltip> and by <GlobalTooltips> (which handles
// every `title` / `data-tooltip` attribute in the app).
//
// Placement (see placePopover): centred below the anchor, shifted left/right to
// stay on screen, flipped above when there isn't room below. It follows the anchor
// while the page scrolls/resizes or the anchor/box change size, and calls
// `onAnchorGone` if the anchor is removed from the page.
export function TooltipBox({ anchor, text, variant = 'default', id, onAnchorGone }) {
  const boxRef = useRef(null);
  // null until measured, so the box is never seen in the wrong place.
  const [position, setPosition] = useState(null);

  const place = useCallback(() => {
    const box = boxRef.current;
    if (!anchor || !box) return;
    if (!anchor.isConnected) {
      onAnchorGone?.();
      return;
    }
    const { width, height } = box.getBoundingClientRect();
    setPosition(placePopover(anchor.getBoundingClientRect(), { width, height }));
  }, [anchor, onAnchorGone]);

  useLayoutEffect(() => {
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const resizeObserver = new ResizeObserver(place);
    if (anchor) resizeObserver.observe(anchor);
    if (boxRef.current) resizeObserver.observe(boxRef.current);
    // Catch the anchor being removed without a mouseout (e.g. a row deleted under it).
    const goneCheck = setInterval(() => {
      if (anchor && !anchor.isConnected) onAnchorGone?.();
    }, 300);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      resizeObserver.disconnect();
      clearInterval(goneCheck);
    };
  }, [anchor, text, place, onAnchorGone]);

  return createPortal(
    <div
      ref={boxRef}
      id={id}
      role="tooltip"
      className={`tooltip tooltip-${variant} tooltip-${position?.placement || 'below'}`}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {text}
      <span className="tooltip-arrow" style={{ left: position?.arrowLeft ?? '50%' }} />
    </div>,
    document.body,
  );
}

// ─── Tooltip ────────────────────────────────────────────────────────────────
// Wraps an element to show `text` in a TooltipBox on hover/focus. Plain `title`
// attributes get the same box automatically (GlobalTooltips); use this wrapper
// for content that isn't a simple string attribute, or to set a `variant`
// (a `data-tooltip-variant` attribute next to a `title` does the same).
//
// Props:
//   text      – the tooltip content (string or node); nothing is shown if empty
//   variant   – 'default' (follows the light/dark theme) | 'info' | 'success'
//               | 'warning' | 'error'
//   delay     – ms to wait after hover before showing (default 200)
//   disabled  – never show the tooltip
//   className / style – applied to the wrapping <span>
//   children  – the element the tooltip belongs to
//
// The wrapper is an inline-flex <span>; give the child no `title` of its own. To
// make a non-focusable child (an icon, a <span>) reachable by keyboard, give it
// tabIndex={0}.
export default function Tooltip({
  text,
  variant = 'default',
  delay = 200,
  disabled = false,
  className,
  style,
  children,
}) {
  const id = useId();
  const anchorRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const hasText = text !== null && text !== undefined && text !== '';

  const show = () => {
    if (disabled || !hasText) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), delay);
  };

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    setOpen(false);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Close if the text goes away or the tooltip is disabled while open.
  useEffect(() => {
    if (open && (disabled || !hasText)) hide();
  }, [open, disabled, hasText, hide]);

  return (
    <>
      <span
        ref={anchorRef}
        className={`tooltip-anchor${className ? ` ${className}` : ''}`}
        style={style}
        // An empty title stops the browser (and GlobalTooltips) using an
        // ancestor's `title` (e.g. a row's "Shift-click to select") here.
        title=""
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </span>
      {open && (
        <TooltipBox anchor={anchorRef.current} text={text} variant={variant} id={id} onAnchorGone={hide} />
      )}
    </>
  );
}
